import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';

import { useFeatureFlags } from '@/components/providers/FeatureFlagsProvider';
import { preLoadDefaultProject } from '@/lib/queries/project';
import { pullRequestQueries } from '@/lib/queries/pullRequest';

export const Route = createFileRoute('/_root/pull-request/')({
  component: RouteComponent,
  beforeLoad: ({ context: { formatMessage } }) => ({
    title: formatMessage({ id: 'global.breadcrumb.pullRequests' }),
  }),
  loader: async ({ context }) => {
    const defaultProject = await preLoadDefaultProject({ context });

    context.queryClient.ensureQueryData(
      pullRequestQueries.list(defaultProject.id),
    );

    return defaultProject;
  },
});

function RouteComponent() {
  const mainProject = Route.useLoaderData();
  const featureFlags = useFeatureFlags();

  const { data: pullRequests = [] } = useSuspenseQuery(
    pullRequestQueries.list(mainProject.id),
  );
  const groupedPrs = pullRequests.reduce<Record<string, typeof pullRequests>>(
    (acc, pr) => {
      const key = `${pr.orgName}/${pr.repoName}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(pr);

      return acc;
    },
    {},
  );
  if (featureFlags.FeaturePRList === false) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <h1 className="text-3xl mb-6">
        <FormattedMessage
          id="pullRequests.title"
          defaultMessage="Pull Requests"
        />
      </h1>
      {Object.entries(groupedPrs).length === 0 ? (
        <div className="flex items-center justify-center min-h-[50vh]">
          <FormattedMessage id="pullRequests.noPullRequest" />
        </div>
      ) : (
        Object.entries(groupedPrs).map(([orgRepo, prs]) => (
          <div key={orgRepo} className="mb-8 p-6 shadow rounded-lg">
            <h2 className="text-2xl font-semibold  border-b pb-2 mb-4">
              {orgRepo}
            </h2>

            <ul className="space-y-4">
              {prs.map(pr => (
                <li
                  key={pr.id}
                  className="flex flex-wrap items-center justify-between p-4 rounded-lg border shadow-sm"
                >
                  <a
                    href={pr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline font-medium"
                  >
                    #{pr.number} {pr.title}
                  </a>

                  <div className="flex items-center space-x-3 text-sm">
                    {pr.nbImpact > 0 && (
                      <span className="text-red-600 font-semibold flex items-center">
                        <FormattedMessage
                          id="pullRequests.impacts"
                          values={{ count: pr.nbImpact }}
                        />
                      </span>
                    )}

                    <span
                      className={`px-2 py-1 rounded-full text-white font-semibold ${
                        pr.state === 'open'
                          ? 'bg-green-500'
                          : pr.state === 'closed'
                            ? 'bg-gray-500'
                            : 'bg-yellow-500'
                      }`}
                    >
                      {pr.state}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </>
  );
}
